import React, { useEffect, useState } from "react";
import { Button, LoadingDots } from "web-ui";

import {
  type Challenge,
  type LeaderboardResponse,
  languageChallengeApi,
} from "../../api/languageChallenge";
import styles from "../../styles/GlobalChallengeAdmin.module.scss";
import { isExternalChallenge } from "../../util/challenges";

import {
  default as ExternalLeaderboard,
  downloadLeaderboardCsv,
} from "./ExternalLeaderboard";

const LeaderboardViewer = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [selectedChallengeId, setSelectedChallengeId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(
    null,
  );
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        setIsLoading(true);
        const externalChallenges = (
          await languageChallengeApi.getChallenges()
        ).filter(c => isExternalChallenge(c.id));
        setChallenges(externalChallenges);
      } catch (error) {
        // Failed to fetch challenges
      } finally {
        setIsLoading(false);
      }
    };
    fetchChallenges();
  }, []);

  useEffect(() => {
    if (!selectedChallengeId) {
      setLeaderboard(null);
      // No-op cleanup so effect return type is consistent (consistent-return).
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }
    let cancelled = false;
    setIsLeaderboardLoading(true);
    setLeaderboard(null);
    languageChallengeApi
      .getExternalLeaderboard(selectedChallengeId, { limit: 10 })
      .then(data => {
        if (!cancelled) {
          setLeaderboard(data);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLeaderboardLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedChallengeId]);

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>View Leaderboard</h2>
      <div className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="leaderboardChallenge">
            Challenge ID <span className={styles.required}>*</span>
          </label>
          <select
            className={styles.select}
            disabled={isLoading}
            id="leaderboardChallenge"
            onChange={e => setSelectedChallengeId(e.target.value)}
            value={selectedChallengeId}
          >
            <option value="">
              {isLoading ? "Loading challenges..." : "Select a challenge"}
            </option>
            {challenges.map(challenge => (
              <option key={challenge.id} value={challenge.id}>
                {challenge.id} - {challenge.description}
              </option>
            ))}
          </select>
        </div>

        {selectedChallengeId && (
          <div className={styles.exportButtonWrap}>
            <Button
              color="macaw"
              disabled={isExporting}
              onClick={async () => {
                setIsExporting(true);
                try {
                  const fullLeaderboard =
                    await languageChallengeApi.getExternalLeaderboard(
                      selectedChallengeId,
                      { includeAll: true },
                    );
                  downloadLeaderboardCsv(fullLeaderboard.leaderboard);
                } finally {
                  setIsExporting(false);
                }
              }}
              type="button"
              variant="solid"
            >
              {isExporting ? <LoadingDots type="button" /> : "Export CSV"}
            </Button>
          </div>
        )}

        {selectedChallengeId && (
          <ExternalLeaderboard
            challengeId={selectedChallengeId}
            leaderboard={leaderboard}
            leaderboardLoading={isLeaderboardLoading}
          />
        )}
      </div>
    </div>
  );
};

export default LeaderboardViewer;
